/**
 * Lib.Common: Platform Communication
 */


import $locale from './locale';


const embedded =
    $build.platform == 'android' ||
    $build.platform == 'desktop' ||
    $build.platform == 'ios';

let listener = [];
let $app = window.$app || {};


if ($app.send === void 0) {
    if ($build.platform == 'android' && $app.sendJSON) {
        // Android may provide sendJSON but not send
        $app.send = data => $app.sendJSON(JSON.stringify(data));
    } else {
        // Use sendToNativeApp where and when available
        let started = Date.now();
        $app.send = data => {
            if (window.sendToNativeApp) {
                ($app.send = window.sendToNativeApp)(data);
            } else if (Date.now() < started + 500) {
                setTimeout(() => { $app.send(data); }, 30);
            } else {
                console.error('$app.send: not ready');
            }
        }
    }
}

if ($app.receive === void 0) {
    $app.receive = data =>
        setTimeout(() => { listener.forEach(obj => obj.receive(data)); });
}

if ($app.addListener === void 0) {
    $app.addListener = obj =>
        listener.indexOf(obj) < 0 && listener.push(obj);
}

if ($app.removeListener === void 0) {
    $app.removeListener = obj =>
        listener = listener.filter(e => e !== obj);
}


/** Polyfill default options */
const option = { ...$app.option };

if (option.englishNumbers === void 0) {
    option.englishNumbers = embedded;
}

if (option.fontSize === void 0) {
    option.fontSize = 16;
}

if (option.language === void 0) {
    option.language = embedded
        ? location.hash.match(/[?&]lang=([\w-]+)(?=&|$)|$/)[1]
        : document.cookie.match(/(?:^|; )IB_LANG=([\w-]+)(?=;|$)|$/)[1];
}

if (option.platform === void 0) {
    option.platform = $build.platform;
}

if (option.privacyMode === void 0) {
    option.privacyMode = false;
}

if (option.theme === void 0) {
    option.theme = embedded
        ? document.cookie.match(/(?:^|; )IB_THEME=(light|dark)(?=;|$)|$/)[1] || '%IB_THEME%'.match(/^(light|dark)$|$/)[1] || location.hash.match(/[?&]theme=(dark)(&|$)|$/)[1] || 'light'
        : document.cookie.match(/(?:^|; )IB_THEME=(light|dark)(?=;|$)|$/)[1] || 'light';
}

if (option.traditionalChineseColors === void 0) {
    option.traditionalChineseColors = false;
}


/** Make options reactive */
const store = {...option};

Object.defineProperty($app, 'option', {
    get: () => option,
    set: value => {
        Object.assign(option, value);
    }
});

Object.defineProperty(option, 'fontSize', {
    get: () => store.fontSize,
    set: fontSize => {
        document.documentElement.style.fontSize = fontSize + 'px';
        store.fontSize = fontSize;
    }
});

Object.defineProperty(option, 'language', {
    get: () => store.language,
    set: language => {
        $locale.use(language);
        // Force English style formatting; leave RTL languages as-is
        if (store.englishNumbers && !/^(ar|he)/.test(language)) {
            $locale.ncurrent = 'en';
        }
        store.language = language;
    }
});

Object.defineProperty(option, 'privacyMode', {
    get: () => store.privacyMode,
    set: privacyMode => {
        store.privacyMode = privacyMode;
    }
});

Object.defineProperty(option, 'theme', {
    get: () => store.theme,
    set: theme => {
        const csso = document.querySelector('link[data-theme]');
        if (csso) {
            const cssn = csso.cloneNode();
            cssn.setAttribute('href', cssn.getAttribute('href').replace('.' + store.theme + '.', '.' + theme + '.'));
            cssn.addEventListener('load', () => { csso.remove(); });
            csso.parentElement.insertBefore(cssn, csso.nextElementSibling);
        }
        store.theme = theme;
    }
});

Object.defineProperty(option, 'traditionalChineseColors', {
    get: () => store.traditionalChineseColors,
    set: traditionalChineseColors => {
        document.body.classList[traditionalChineseColors ? 'add' : 'remove']('_trcc');
        store.traditionalChineseColors = traditionalChineseColors;
    }
});

if (embedded) {
    addEventListener('hashchange', () => {
        const theme = location.hash.match(/[?&]theme=(.+?)(&|$)/);
        if (theme) {
            option.theme = theme[1];
        }
    });
}


/** Make options reactive in Vue */
if (typeof Vue !== 'undefined') {
    Object.keys(option).forEach(key => {
        Vue.util.defineReactive(option, key, option[key]);
    });
}


/** Default implementation of `sendToWebApp`; may be overriden by application */
window.sendToWebApp = msg => {
    setTimeout(() => {
        $app.receive(msg);
    });
};


/** Initial language */
$locale.use(option.language);


/** Initial font size */
if (option.fontSize != parseFloat(getComputedStyle(document.documentElement).getPropertyValue('font-size'))) {
    document.documentElement.style.fontSize = option.fontSize + 'px';
}

/** Initial 'Chinese colors' state */
if (option.traditionalChineseColors) {
    document.body.classList.add('_trcc');
}


export default $app;
